import React, {
  forwardRef,
  ReactElement,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Form, Input, Select, DatePicker, Row, Col, Tag } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import styled from "styled-components";
import { usePost } from "../../hooks/usePost";
import {
  CompassGreenButton,
  CompassWhiteButton,
} from "../../components/buttons/styledComponents";
import { useToast } from "../../store/ToastProvider";
import { SelectableWithLogo } from "../../components/common/Modal";
import { InlinePrefill } from "../../components/common/InlinePrefill";
import { AIPrefillEngagementResponse } from "../../types/compassAITypes";
import {
  EngagementCategory,
  EngagementType,
  CustomTag,
  TagInput,
  UpsertEngagementRecordRequest,
  UpsertEngagementRecordResponse,
} from "../../models/stewardshipModels";
import { MilestoneKey } from "../../enums/Status";
import { getSdgIcon } from "../../utils/formattingUtils";
import {
  getMilestoneSelectOptions,
  isCorporateIssueEngagement,
} from "../../utils/engagementMilestones";
import { Icon } from "../../components/visuals/styledComponents";
import { MESSAGES } from "../../messages";
import { graphPaletteV2, greyText } from "../../styles";

enum EngagementStatus {
  Underway = "Underway",
  Complete = "Complete",
  Pending = "Pending",
  Discontinued = "Discontinued",
  CorporateIssue = "Corporate Issue",
}

export enum FormMode {
  Create = "create",
  Edit = "edit",
}

enum FormFieldName {
  ENGAGEMENT_RECORD_ID = "engagement_record_id",
  COMPANY = "company",
  MANAGER = "manager",
  CATEGORY = "category",
  THEME = "theme",
  SUB_THEME = "subTheme",
  OBJECTIVE_TITLE = "objectiveTitle",
  OBJECTIVE_DESCRIPTION = "objectiveDescription",
  SDGS = "sdgs",
  START_DATE = "startDate",
  END_DATE = "endDate",
  MILESTONE = "milestone",
  CURRENT_STATUS = "currentStatus",
  LATEST_UPDATE = "latestUpdate",
  TAGS = "tags",
}

// SDG Configuration - derived from centralized messages
const SDG_NAMES = MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.SDG_NAMES;
const SDG_DATA = Object.entries(SDG_NAMES).map(([id, name]) => ({
  id: Number(id),
  name,
}));

const { TextArea } = Input;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledFormWrapper = styled.div`
  width: 100%;

  .ant-select-selector,
  .ant-picker,
  .ant-input,
  .ant-input-textarea {
    width: 100%;
  }

  .ant-form-item-label > label {
    color: ${greyText} !important;
  }
`;

const PRESET_TAG_COLORS = graphPaletteV2;

// Styled components for tag display in Select
const TagBadge = styled.span<{ $bgColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: ${({ $bgColor }): string => $bgColor};
  color: white;
  font-weight: 500;
  font-size: 12px;
`;

const TagOptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CreateTagDivider = styled.div`
  border-top: 1px solid #f0f0f0;
  margin: 8px 0;
  padding-top: 8px;
`;

const ColorGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const ColorSwatch = styled.div<{ $color: string; $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: ${({ $color }): string => $color};
  cursor: pointer;
  border: 2px solid
    ${({ $isSelected }): string => ($isSelected ? "#1890ff" : "transparent")};
  transition:
    transform 0.15s,
    border-color 0.15s;

  &:hover {
    transform: scale(1.1);
  }
`;

const NewTagInputContainer = styled.div`
  padding: 8px 12px;
`;

const NewTagInputRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SmallGreenButton = styled(CompassGreenButton)`
  padding: 4px 16px;
  font-size: 13px;
  height: auto;

  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(0.9);
  }
`;

const CreateTagButton = styled(CompassWhiteButton)`
  padding: 6px 12px;
  font-size: 13px;
  height: auto;
  width: 100%;

  margin-right: 0;
  gap: 6px;

  &:hover {
    opacity: 0.85;
  }
`;

const TAXONOMY = {
  Environmental: {
    Circular_Economy_and_Zero_Pollution: [
      "Circular_Economy_and_Waste",
      "Pollution",
    ],
    Climate_Change: [
      "Climate_Opportunities",
      "GHG_Emissions_Reduction",
      "Governance_and_Transparency",
      "Physical_Risk",
    ],
    Natural_Resource_Stewardship: [
      "Antimicrobial_Resistance",
      "Biodiversity_and_Sustainable_Food_Systems",
      "Water_Stress",
    ],
  },
  Social: {
    Human_Capital: [
      "DEI_and_Innovation",
      "Employment_Terms_and_Conditions",
      "Health_Safety_and_Wellbeing",
    ],
    Human_and_Labour_Rights: [
      "Access_and_Affordability",
      "Digital_Rights",
      "High_Geographic_Risks",
      "Indigenous_and_Community_Rights",
      "Supply_Chain_Rights",
    ],
    Wider_Societal_Impacts: [
      "Anti-bribery_and_Corruption",
      "Conduct_and_Ethics",
      "Responsible_Tax_Practices",
      "Safe_Products_and_Services",
    ],
  },
  Strategy: {
    Purpose_Strategy_and_Policies: [
      "Capital_Allocation",
      "Long-term_Sustainable_Strategy",
    ],
    Risk_Management: ["Cyber_Security", "Enterprise_Risk_Practices"],
  },
  Governance: {
    Board_Effectiveness: [
      "Board_and_Mgt_Effectiveness",
      "Board_Composition_and_Structure",
      "Succession_and_Stability",
    ],
    Executive_Remuneration: ["Pay_Design_and_Transparency", "Pay_Outcomes"],
    Investor_Protection_and_Rights: [
      "Basic_Shareholder_Rights",
      "Debtholder_Rights",
      "Minority_Protections",
    ],
  },
} as const;

type Taxonomy = typeof TAXONOMY;
type CategoryKey = keyof Taxonomy;

const ALL_THEMES: string[] = (Object.keys(TAXONOMY) as CategoryKey[]).flatMap(
  (cat) => Object.keys(TAXONOMY[cat]),
);
const ALL_SUBS: string[] = (Object.keys(TAXONOMY) as CategoryKey[]).flatMap(
  (cat) => Object.values(TAXONOMY[cat]).flat(),
);

interface EngagementRecordFormValues {
  [FormFieldName.ENGAGEMENT_RECORD_ID]?: string; // For edit mode
  [FormFieldName.COMPANY]: string;
  [FormFieldName.MANAGER]: string;
  [FormFieldName.CATEGORY]: EngagementType;
  [FormFieldName.THEME]: string;
  [FormFieldName.SUB_THEME]: string;
  [FormFieldName.OBJECTIVE_TITLE]: string;
  [FormFieldName.OBJECTIVE_DESCRIPTION]?: string;
  [FormFieldName.SDGS]?: number[]; // Array of SDG IDs (1-17)
  [FormFieldName.START_DATE]: Dayjs;
  [FormFieldName.END_DATE]?: Dayjs;
  [FormFieldName.MILESTONE]?: string;
  [FormFieldName.CURRENT_STATUS]: EngagementStatus;
  [FormFieldName.LATEST_UPDATE]?: string;
  [FormFieldName.TAGS]?: TagInput[]; // Array of tag inputs (existing or new)
}

const CATEGORY_OPTIONS = Object.values(EngagementType);
const STATUS_OPTIONS = Object.values(EngagementStatus);

interface FormFieldConfig {
  name: keyof EngagementRecordFormValues;
  label: string;
  placeholder?: string;
  required?: boolean;
  requiredMessage?: string;
  rows?: number;
}

interface SelectFieldConfig extends FormFieldConfig {
  options?: Array<{ value: string; label: string }>;
  loading?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  showSearch?: boolean;
}

const FORM_FIELDS: Partial<
  Record<FormFieldName, FormFieldConfig | SelectFieldConfig>
> = {
  [FormFieldName.COMPANY]: {
    name: FormFieldName.COMPANY,
    label: "Company",
    placeholder: "Select a company",
    required: true,
    requiredMessage: "Please select a company",
    allowClear: true,
    showSearch: true,
  },
  [FormFieldName.CATEGORY]: {
    name: FormFieldName.CATEGORY,
    label: "Category",
    placeholder: "Select category",
    required: true,
    requiredMessage: "Select a category",
    options: CATEGORY_OPTIONS.map((v) => ({ value: v, label: v })),
    allowClear: true,
  },
  [FormFieldName.THEME]: {
    name: FormFieldName.THEME,
    label: "Theme",
    placeholder: "Select theme",
    required: true,
    requiredMessage: "Please select a theme",
    allowClear: true,
    showSearch: true,
  },
  [FormFieldName.SUB_THEME]: {
    name: FormFieldName.SUB_THEME,
    label: "Sub-theme",
    placeholder: "Select sub-theme",
    required: true,
    requiredMessage: "Please select a sub-theme",
    allowClear: true,
    showSearch: true,
  },
  [FormFieldName.MANAGER]: {
    name: FormFieldName.MANAGER,
    label: "Manager",
    placeholder: "Select a manager",
    required: true,
    requiredMessage: "Please select a manager",
    allowClear: true,
    showSearch: true,
  },
  [FormFieldName.OBJECTIVE_TITLE]: {
    name: FormFieldName.OBJECTIVE_TITLE,
    label: "Engagement Name",
    placeholder: "e.g., Auditor rotation",
    required: true,
    requiredMessage: "Please enter the engagement name",
  },
  [FormFieldName.OBJECTIVE_DESCRIPTION]: {
    name: FormFieldName.OBJECTIVE_DESCRIPTION,
    label: "Objective Description",
    placeholder: "Describe the purpose and expected outcome",
    rows: 4,
  },
  [FormFieldName.SDGS]: {
    name: FormFieldName.SDGS,
    label: "Sustainable Development Goals (SDGs)",
    placeholder: "Select relevant SDGs",
    allowClear: true,
    showSearch: true,
  },
  [FormFieldName.START_DATE]: {
    name: FormFieldName.START_DATE,
    label: "Start Date",
    required: true,
    requiredMessage: "Select a start date",
  },
  [FormFieldName.END_DATE]: {
    name: FormFieldName.END_DATE,
    label: "Target End Date",
  },
  [FormFieldName.MILESTONE]: {
    name: FormFieldName.MILESTONE,
    label: "Milestone",
  },
  [FormFieldName.CURRENT_STATUS]: {
    name: FormFieldName.CURRENT_STATUS,
    label: "Status",
    options: STATUS_OPTIONS.map((v) => ({ value: v, label: v })),
  },
  [FormFieldName.LATEST_UPDATE]: {
    name: FormFieldName.LATEST_UPDATE,
    label: "Latest Update",
    placeholder: "e.g., Implemented new audit practices…",
    rows: 3,
  },
  [FormFieldName.TAGS]: {
    name: FormFieldName.TAGS,
    label: "Tags",
    placeholder: "Add tags to categorize this engagement",
  },
} as const;

const getFieldConfig = (
  fieldName: FormFieldName,
): FormFieldConfig | SelectFieldConfig => {
  const config = FORM_FIELDS[fieldName];
  if (!config) {
    throw new Error(`Field config not found for ${fieldName}`);
  }
  return config;
};

const prettify = (s: string): string => s.replace(/_/g, " ");
const isCatKey = (c: string): c is CategoryKey =>
  Object.prototype.hasOwnProperty.call(TAXONOMY, c);

const getThemesForCategory = (cat?: string): string[] => {
  if (!cat || !isCatKey(cat)) return [];
  return Object.keys(TAXONOMY[cat]);
};

const getSubsForTheme = (theme?: string): string[] => {
  if (!theme) return [];
  for (const cat of Object.keys(TAXONOMY) as CategoryKey[]) {
    if (Object.prototype.hasOwnProperty.call(TAXONOMY[cat], theme)) {
      // @ts-expect-error theme is a key of this category
      return TAXONOMY[cat][theme] ?? [];
    }
  }
  return [];
};

const themeToCategory = new Map<string, CategoryKey>(
  (Object.keys(TAXONOMY) as CategoryKey[]).flatMap((cat) =>
    Object.keys(TAXONOMY[cat]).map((t) => [t, cat] as const),
  ),
);

const subToTheme = new Map<string, string>(
  (Object.keys(TAXONOMY) as CategoryKey[]).flatMap((cat) =>
    Object.entries(TAXONOMY[cat]).flatMap(([theme, subs]) =>
      subs.map((s: string) => [s, theme] as const),
    ),
  ),
);

const subToCategory = new Map<string, CategoryKey>(
  (Object.keys(TAXONOMY) as CategoryKey[]).flatMap((cat) =>
    Object.values(TAXONOMY[cat]).flatMap((subs) =>
      subs.map((s: string) => [s, cat] as const),
    ),
  ),
);

export interface EngagementRecordFormRef {
  submit: () => void;
  isLoading: boolean;
}

type EngagementRecordFormInitialData = Partial<EngagementRecordFormValues> & {
  engagementCategory?: EngagementCategory | string | null;
  startDate?: Dayjs | string | null;
  endDate?: Dayjs | string | null;
};

interface EngagementRecordFormProps {
  initialData?: EngagementRecordFormInitialData;
  mode?: FormMode;
  disabledFields?: Array<keyof EngagementRecordFormValues>;
  onSuccess?: () => void; // Callback to close modal and reset state
  onLoadingChange?: (isLoading: boolean) => void; // Callback when loading state changes
  onValidityChange?: (isValid: boolean) => void; // Callback when required field validity changes
}

const DEFAULT_VALUES: Partial<EngagementRecordFormValues> = {
  currentStatus: EngagementStatus.Pending,
  milestone: MilestoneKey.M1,
};

const coerceDateValue = (value?: Dayjs | string | null): Dayjs | undefined => {
  if (!value) return undefined;
  if (dayjs.isDayjs(value)) return value;

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : undefined;
};

const formatDateValue = (value?: Dayjs | string | null): string => {
  const parsed = coerceDateValue(value);
  return parsed ? parsed.format("YYYY-MM-DD") : "";
};

// Automatically derive non-modifiable fields from required field configurations
const NON_MODIFIABLE_FIELDS: Array<keyof EngagementRecordFormValues> = (
  Object.entries(FORM_FIELDS) as Array<
    [keyof EngagementRecordFormValues, FormFieldConfig | SelectFieldConfig]
  >
)
  .filter(([, config]) => config.required)
  .map(([fieldName]) => fieldName);

export const EngagementRecordForm = forwardRef<
  EngagementRecordFormRef,
  EngagementRecordFormProps
>(
  (
    {
      initialData,
      mode = FormMode.Create,
      disabledFields = [],
      onSuccess,
      onLoadingChange,
      onValidityChange,
    },
    ref,
  ) => {
    const [form] = Form.useForm<EngagementRecordFormValues>();
    const { addToast } = useToast();
    const isCorporateIssue = isCorporateIssueEngagement(
      initialData?.engagementCategory,
    );

    // Watch company field for AI prefill
    const selectedCompany = Form.useWatch("company", form);

    // Watch all required fields for validity tracking
    const watchedCompany = Form.useWatch("company", form);
    const watchedCategory = Form.useWatch("category", form);
    const watchedTheme = Form.useWatch("theme", form);
    const watchedSubTheme = Form.useWatch("subTheme", form);
    const watchedManager = Form.useWatch("manager", form);
    const watchedObjectiveTitle = Form.useWatch("objectiveTitle", form);
    const watchedStartDate = Form.useWatch("startDate", form);

    // Check if all required fields are filled
    useEffect(() => {
      const isValid = Boolean(
        watchedCompany &&
        watchedCategory &&
        watchedTheme &&
        watchedSubTheme &&
        watchedManager &&
        watchedObjectiveTitle &&
        watchedStartDate,
      );
      onValidityChange?.(isValid);
    }, [
      watchedCompany,
      watchedCategory,
      watchedTheme,
      watchedSubTheme,
      watchedManager,
      watchedObjectiveTitle,
      watchedStartDate,
      onValidityChange,
    ]);

    const getDisabledFields = (): Array<keyof EngagementRecordFormValues> => {
      if (mode === FormMode.Edit) {
        return [...NON_MODIFIABLE_FIELDS, ...disabledFields];
      }
      return disabledFields;
    };

    const disabledFieldsList = getDisabledFields();
    const isFieldDisabled = (
      fieldName: keyof EngagementRecordFormValues,
    ): boolean => {
      return (
        disabledFieldsList.includes(fieldName) &&
        fieldName !== FormFieldName.START_DATE
      );
    };

    const {
      data: managers,
      sendData: fetchManagers,
      isLoading: isManagerLoading,
    } = usePost<
      {
        manager_id: string;
        manager_name: string;
        email: string;
        image: string;
      }[],
      null
    >();

    const {
      data: companies,
      sendData: fetchCompanies,
      isLoading: isCompaniesLoading,
    } = usePost<{ name: string; compass_cid: string; image: string }[], null>();

    const {
      data: customTags,
      sendData: fetchCustomTags,
      isLoading: isCustomTagsLoading,
    } = usePost<CustomTag[], null>();

    const {
      sendData: upsertEngagementRecord,
      isLoading: isUpserting,
      error: upsertError,
    } = usePost<
      UpsertEngagementRecordResponse,
      UpsertEngagementRecordRequest
    >();

    // Expose form methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        submit: (): void => form.submit(),
        isLoading: isUpserting,
      }),
      [form, isUpserting],
    );

    // Notify parent of loading state changes
    useEffect(() => {
      onLoadingChange?.(isUpserting);
    }, [isUpserting, onLoadingChange]);

    useEffect(() => {
      (async (): Promise<void> => {
        await Promise.all([
          fetchManagers("GET_ALL_MANAGERS", null),
          fetchCompanies("GET_COMPANIES", null),
          fetchCustomTags("GET_CUSTOM_TAGS", null),
        ]);
      })();
    }, [fetchManagers, fetchCompanies, fetchCustomTags]);

    useEffect(() => {
      if (upsertError) {
        const isUpdate = mode === FormMode.Edit;
        const errorMessage = isUpdate
          ? MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_RECORD
              .UPDATE_ERROR
          : MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_RECORD
              .CREATE_ERROR;
        addToast(errorMessage, "error");
      }
    }, [upsertError, mode, addToast]);

    // --- atomic backfill handlers (respect user's explicit pick) ---
    const onCategoryChange = (cat?: string): void => {
      const patch: Partial<EngagementRecordFormValues> = {
        category: cat as EngagementType,
      };
      const themes = getThemesForCategory(cat);
      const currentTheme = form.getFieldValue("theme");

      if (!currentTheme || !themes.includes(currentTheme)) {
        patch.theme = undefined;
        patch.subTheme = undefined;
      }
      if (!patch.theme && themes.length === 1) {
        patch.theme = themes[0];
        const subs = getSubsForTheme(themes[0]);
        if (subs.length === 1) patch.subTheme = subs[0];
      }
      form.setFieldsValue(patch);
    };

    const onThemeChange = (theme?: string): void => {
      const patch: Partial<EngagementRecordFormValues> = { theme };
      const cat = theme ? themeToCategory.get(theme) : undefined;
      if (cat) patch.category = cat as EngagementType;

      const subs = getSubsForTheme(theme);
      const currentSub = form.getFieldValue("subTheme");
      if (!currentSub || !subs.includes(currentSub)) patch.subTheme = undefined;
      if (!patch.subTheme && subs.length === 1) patch.subTheme = subs[0];

      form.setFieldsValue(patch);
    };

    const onSubThemeChange = (sub?: string): void => {
      const patch: Partial<EngagementRecordFormValues> = { subTheme: sub };
      const theme = sub ? subToTheme.get(sub) : undefined;
      const cat = sub ? subToCategory.get(sub) : undefined;
      if (theme) patch.theme = theme;
      if (cat) patch.category = cat as EngagementType;
      form.setFieldsValue(patch);
    };

    /**
     * Handle AI prefill result - populate form with extracted fields.
     */
    const handlePrefillSuccess = (
      result: AIPrefillEngagementResponse,
    ): void => {
      const { extracted_fields, fields_found } = result;
      const patch: Partial<EngagementRecordFormValues> = {};

      // Map API field names to form field names
      if (fields_found.includes("category") && extracted_fields.category) {
        const cat = extracted_fields.category as EngagementType;
        if (Object.values(EngagementType).includes(cat)) {
          patch.category = cat;
        }
      }

      if (fields_found.includes("theme") && extracted_fields.theme) {
        // Normalize theme name (replace spaces with underscores)
        const theme = extracted_fields.theme.replace(/ /g, "_");
        if (ALL_THEMES.includes(theme)) {
          patch.theme = theme;
        }
      }

      if (fields_found.includes("sub_theme") && extracted_fields.sub_theme) {
        const subTheme = extracted_fields.sub_theme.replace(/ /g, "_");
        if (ALL_SUBS.includes(subTheme)) {
          patch.subTheme = subTheme;
        }
      }

      if (
        fields_found.includes("objective_title") &&
        extracted_fields.objective_title
      ) {
        patch.objectiveTitle = extracted_fields.objective_title;
      }

      if (
        fields_found.includes("objective_description") &&
        extracted_fields.objective_description
      ) {
        patch.objectiveDescription = extracted_fields.objective_description;
      }

      if (fields_found.includes("start_date") && extracted_fields.start_date) {
        const startDate = dayjs(extracted_fields.start_date);
        if (startDate.isValid()) {
          patch.startDate = startDate;
        }
      }

      if (fields_found.includes("end_date") && extracted_fields.end_date) {
        const endDate = dayjs(extracted_fields.end_date);
        if (endDate.isValid()) {
          patch.endDate = endDate;
        }
      }

      if (fields_found.includes("milestone") && extracted_fields.milestone) {
        const milestone = extracted_fields.milestone;
        if (
          [
            MilestoneKey.M1,
            MilestoneKey.M2,
            MilestoneKey.M3,
            MilestoneKey.M4,
            MilestoneKey.CI,
          ].includes(milestone as MilestoneKey)
        ) {
          patch.milestone = milestone;
        }
      }

      if (fields_found.includes("status") && extracted_fields.status) {
        const status = extracted_fields.status as EngagementStatus;
        if (Object.values(EngagementStatus).includes(status)) {
          patch.currentStatus = status;
        }
      }

      if (fields_found.includes("sdgs") && extracted_fields.sdgs?.length) {
        // Filter to valid SDG IDs (1-17)
        const validSdgs = extracted_fields.sdgs.filter(
          (n) => n >= 1 && n <= 17,
        );
        if (validSdgs.length > 0) {
          patch.sdgs = validSdgs;
        }
      }

      // Check extracted_fields directly as latest_update may not be in fields_found
      if (extracted_fields.latest_update) {
        patch.latestUpdate = extracted_fields.latest_update;
      }

      // Check extracted_fields directly as tags may not be in fields_found
      if (extracted_fields.tags?.length) {
        // Convert string tags to TagInput format (as new tags with default color)
        patch.tags = extracted_fields.tags.map((tagName, index) => ({
          display_name: tagName,
          color: PRESET_TAG_COLORS[index % PRESET_TAG_COLORS.length],
        }));
      }

      // Apply the patch to form
      if (Object.keys(patch).length > 0) {
        form.setFieldsValue(patch);
        addToast(
          `Pre-filled ${fields_found.length} field${fields_found.length !== 1 ? "s" : ""}`,
          "success",
        );
      }
    };

    const managerOptions = useMemo(() => {
      if (!managers)
        return [] as { key: string; value: string; label: JSX.Element }[];
      return managers.map((m) => ({
        key: m.manager_id,
        value: m.manager_id,
        label: SelectableWithLogo(m.manager_id, m.manager_name, m.image),
      }));
    }, [managers]);

    const companyOptions = useMemo(() => {
      if (!companies)
        return [] as { key: string; value: string; label: JSX.Element }[];
      return companies
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({
          key: c.compass_cid,
          value: c.compass_cid,
          label: SelectableWithLogo(c.compass_cid, c.name, c.image),
        }));
    }, [companies]);

    const sdgOptions = useMemo(() => {
      return SDG_DATA.map((sdg) => ({
        key: sdg.id.toString(),
        value: sdg.id,
        label: SDGOption(sdg.id, sdg.name),
      }));
    }, []);

    const onFinish = async (
      values: EngagementRecordFormValues,
    ): Promise<void> => {
      const requestPayload: UpsertEngagementRecordRequest = {
        engagement_record_id: initialData?.engagement_record_id,
        company: values.company,
        manager: values.manager,
        category: values.category,
        theme: values.theme,
        subTheme: values.subTheme,
        objectiveTitle: values.objectiveTitle,
        objectiveDescription: values.objectiveDescription,
        sdgs: values.sdgs,
        startDate: formatDateValue(values.startDate),
        endDate: formatDateValue(values.endDate) || undefined,
        milestone: values.milestone || MilestoneKey.M1,
        currentStatus: values.currentStatus || EngagementStatus.Pending,
        latestUpdate: values.latestUpdate,
        tags: values.tags && values.tags.length > 0 ? values.tags : null,
      };

      const result = await upsertEngagementRecord(
        "UPSERT_ENGAGEMENT_RECORD",
        requestPayload,
      );

      if (result) {
        const isUpdate = mode === FormMode.Edit;
        const successMessage = isUpdate
          ? MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_RECORD
              .UPDATE_SUCCESS
          : MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ENGAGEMENT_RECORD
              .CREATE_SUCCESS;

        addToast(successMessage, "success");

        // Close modal and reset state first
        onSuccess?.();

        if (mode === FormMode.Create) {
          form.resetFields();
        }
      }
    };

    // Merge initial data with defaults
    const formInitialValues = {
      ...DEFAULT_VALUES,
      ...initialData,
      milestone: isCorporateIssue ? MilestoneKey.CI : initialData?.milestone,
      startDate: coerceDateValue(initialData?.startDate),
      endDate: coerceDateValue(initialData?.endDate),
    };

    return (
      <FormContainer>
        <StyledFormWrapper>
          <Form<EngagementRecordFormValues>
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={formInitialValues}
            size="large"
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="company"
                  label="Company"
                  rules={[
                    { required: true, message: "Please select a company" },
                  ]}
                >
                  <Select
                    placeholder="Select a company"
                    options={companyOptions}
                    loading={isCompaniesLoading}
                    disabled={
                      isCompaniesLoading ||
                      isFieldDisabled(FormFieldName.COMPANY)
                    }
                    allowClear
                    showSearch
                    optionLabelProp="label"
                    filterOption={(input, option): boolean => {
                      const company = companies?.find(
                        (c) => c.compass_cid === option?.value,
                      );
                      return company
                        ? company.name
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        : false;
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="category"
                  label="Category"
                  rules={[{ required: true, message: "Select a category" }]}
                >
                  <Select
                    placeholder="Select category"
                    options={CATEGORY_OPTIONS.map((v) => ({
                      value: v,
                      label: v,
                    }))}
                    allowClear
                    disabled={isFieldDisabled(FormFieldName.CATEGORY)}
                    onChange={onCategoryChange}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Smart Fill - slides in under Company when selected (Create mode only) */}
            {mode === FormMode.Create && (
              <InlinePrefill
                companyId={selectedCompany}
                onSuccess={handlePrefillSuccess}
              />
            )}
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur): boolean =>
                prev.category !== cur.category
              }
            >
              {({ getFieldValue }): ReactElement => {
                const cat = getFieldValue("category");
                const themeOpts = isCatKey(cat)
                  ? getThemesForCategory(cat)
                  : cat === "Governance"
                    ? []
                    : ALL_THEMES;
                return (
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="theme"
                        label="Theme"
                        rules={[
                          { required: true, message: "Please select a theme" },
                        ]}
                      >
                        <Select
                          placeholder="Select theme"
                          options={themeOpts.map((t) => ({
                            value: t,
                            label: prettify(t),
                          }))}
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          onChange={onThemeChange}
                          disabled={isFieldDisabled(FormFieldName.THEME)}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                      <Form.Item
                        noStyle
                        shouldUpdate={(p, c): boolean =>
                          p.category !== c.category || p.theme !== c.theme
                        }
                      >
                        {({ getFieldValue: gf }): ReactElement => {
                          const theme = gf("theme");
                          const cat2 = gf("category");
                          const subs = theme
                            ? getSubsForTheme(theme)
                            : isCatKey(cat2)
                              ? Object.values(TAXONOMY[cat2]).flat()
                              : ALL_SUBS;
                          return (
                            <Form.Item
                              name="subTheme"
                              label="Sub-theme"
                              rules={[
                                {
                                  required: true,
                                  message: "Please select a sub-theme",
                                },
                              ]}
                            >
                              <Select
                                placeholder="Select sub-theme"
                                options={subs.map((s) => ({
                                  value: s,
                                  label: prettify(s),
                                }))}
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                onChange={onSubThemeChange}
                                disabled={isFieldDisabled(
                                  FormFieldName.SUB_THEME,
                                )}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }}
            </Form.Item>

            <Form.Item
              name="manager"
              label="Manager"
              rules={[{ required: true, message: "Please select a manager" }]}
            >
              <Select
                placeholder="Select a manager"
                options={managerOptions}
                loading={isManagerLoading}
                disabled={
                  isManagerLoading || isFieldDisabled(FormFieldName.MANAGER)
                }
                allowClear
                showSearch
                optionLabelProp="label"
                filterOption={(input, option): boolean => {
                  const manager = managers?.find(
                    (m) => m.manager_id === option?.value,
                  );
                  return manager
                    ? manager.manager_name
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    : false;
                }}
              />
            </Form.Item>

            <Form.Item
              name={getFieldConfig(FormFieldName.OBJECTIVE_TITLE).name}
              label={getFieldConfig(FormFieldName.OBJECTIVE_TITLE).label}
              rules={[
                {
                  required: getFieldConfig(FormFieldName.OBJECTIVE_TITLE)
                    .required,
                  message: getFieldConfig(FormFieldName.OBJECTIVE_TITLE)
                    .requiredMessage,
                },
              ]}
            >
              <Input
                placeholder={
                  getFieldConfig(FormFieldName.OBJECTIVE_TITLE).placeholder
                }
                disabled={isFieldDisabled(FormFieldName.OBJECTIVE_TITLE)}
              />
            </Form.Item>

            <Form.Item
              name={getFieldConfig(FormFieldName.OBJECTIVE_DESCRIPTION).name}
              label={getFieldConfig(FormFieldName.OBJECTIVE_DESCRIPTION).label}
            >
              <TextArea
                placeholder={
                  getFieldConfig(FormFieldName.OBJECTIVE_DESCRIPTION)
                    .placeholder
                }
                rows={getFieldConfig(FormFieldName.OBJECTIVE_DESCRIPTION).rows}
              />
            </Form.Item>

            <Form.Item
              name={getFieldConfig(FormFieldName.SDGS).name}
              label={getFieldConfig(FormFieldName.SDGS).label}
            >
              <Select
                mode="multiple"
                placeholder={getFieldConfig(FormFieldName.SDGS).placeholder}
                options={sdgOptions}
                allowClear={
                  (getFieldConfig(FormFieldName.SDGS) as SelectFieldConfig)
                    .allowClear
                }
                showSearch={
                  (getFieldConfig(FormFieldName.SDGS) as SelectFieldConfig)
                    .showSearch
                }
                optionLabelProp="label"
                filterOption={(input, option): boolean => {
                  const sdg = SDG_DATA.find((s) => s.id === option?.value);
                  return sdg
                    ? sdg.name.toLowerCase().includes(input.toLowerCase()) ||
                        sdg.id.toString().includes(input)
                    : false;
                }}
              />
            </Form.Item>

            {/* Tags Select */}
            <Form.Item
              name={FormFieldName.TAGS}
              label={getFieldConfig(FormFieldName.TAGS).label}
            >
              <TagsSelect
                availableTags={customTags || []}
                isLoading={isCustomTagsLoading}
              />
            </Form.Item>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="startDate"
                  label="Start Date"
                  rules={[{ required: true, message: "Select a start date" }]}
                >
                  <DatePicker
                    disabled={isFieldDisabled(FormFieldName.START_DATE)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="endDate" label="Target End Date">
                  <DatePicker />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="milestone" label="Milestone">
              <Select
                options={getMilestoneSelectOptions(isCorporateIssue)}
                disabled={isCorporateIssue}
              />
            </Form.Item>

            <Form.Item
              name={getFieldConfig(FormFieldName.CURRENT_STATUS).name}
              label={getFieldConfig(FormFieldName.CURRENT_STATUS).label}
            >
              <Select
                options={
                  (
                    getFieldConfig(
                      FormFieldName.CURRENT_STATUS,
                    ) as SelectFieldConfig
                  ).options
                }
              />
            </Form.Item>

            <Form.Item
              name={getFieldConfig(FormFieldName.LATEST_UPDATE).name}
              label={getFieldConfig(FormFieldName.LATEST_UPDATE).label}
            >
              <TextArea
                placeholder={
                  getFieldConfig(FormFieldName.LATEST_UPDATE).placeholder
                }
                rows={getFieldConfig(FormFieldName.LATEST_UPDATE).rows}
              />
            </Form.Item>
          </Form>
        </StyledFormWrapper>
      </FormContainer>
    );
  },
);

EngagementRecordForm.displayName = "EngagementRecordForm";

// Helper to render tag option with color badge
const TagOption = (tag: CustomTag): JSX.Element => (
  <TagOptionContainer>
    <TagBadge $bgColor={tag.color}>{tag.display_name}</TagBadge>
  </TagOptionContainer>
);

// Custom TagsSelect component using Ant Design Select
interface TagsSelectProps {
  value?: TagInput[];
  onChange?: (tags: TagInput[]) => void;
  availableTags?: CustomTag[];
  isLoading?: boolean;
}

const TagsSelect: React.FC<TagsSelectProps> = ({
  value = [],
  onChange,
  availableTags = [],
  isLoading = false,
}) => {
  const [newTagName, setNewTagName] = useState("");
  const [selectedColor, setSelectedColor] = useState(PRESET_TAG_COLORS[0]);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Create a map for quick lookup of available tags
  const tagMap = useMemo(() => {
    return new Map(availableTags.map((t) => [t.id, t]));
  }, [availableTags]);

  // Get currently selected tag IDs (for existing tags)
  const selectedTagIds = useMemo(() => {
    return value.filter((t) => t.id).map((t) => t.id as string);
  }, [value]);

  // Get new tags that were created but not yet in availableTags
  const newTags = useMemo(() => {
    return value.filter((t) => t.display_name && t.color && !t.id);
  }, [value]);

  // Build options for the Select
  const tagOptions = useMemo(() => {
    const options = availableTags.map((tag) => ({
      key: tag.id,
      value: tag.id,
      label: TagOption(tag),
      tag,
    }));

    // Add new tags that were created in this session
    newTags.forEach((newTag, index) => {
      if (!newTag.display_name || !newTag.color) return;
      const tempId = `__new_${index}_${newTag.display_name}`;
      options.push({
        key: tempId,
        value: tempId,
        label: TagOption({
          id: tempId,
          display_name: newTag.display_name,
          color: newTag.color,
        }),
        tag: {
          id: tempId,
          display_name: newTag.display_name,
          color: newTag.color,
        },
      });
    });

    return options;
  }, [availableTags, newTags]);

  // Handle selection change
  const handleChange = (selectedValues: string[]): void => {
    const newTagInputs: TagInput[] = selectedValues.map((val) => {
      // Check if it's a newly created tag (temp ID)
      if (val.startsWith("__new_")) {
        const newTag = newTags.find(
          (t, i) => `__new_${i}_${t.display_name}` === val,
        );
        if (newTag) {
          return { display_name: newTag.display_name, color: newTag.color };
        }
      }
      // Otherwise it's an existing tag ID
      return { id: val };
    });
    onChange?.(newTagInputs);
  };

  // Handle creating a new tag
  const handleCreateTag = (): void => {
    if (!newTagName.trim()) return;

    const newTag: TagInput = {
      display_name: newTagName.trim(),
      color: selectedColor,
    };

    onChange?.([...value, newTag]);
    setNewTagName("");
    setSelectedColor(PRESET_TAG_COLORS[0]);
    setShowCreateForm(false);
  };

  // Custom dropdown render to add "Create new tag" option
  const dropdownRender = (menu: React.ReactElement): React.ReactElement => (
    <div>
      {menu}
      <CreateTagDivider />
      {!showCreateForm ? (
        <div style={{ padding: "8px 12px" }}>
          <CreateTagButton
            onClick={(e): void => {
              e.stopPropagation();
              setShowCreateForm(true);
            }}
          >
            <PlusOutlined />
            Create new tag
          </CreateTagButton>
        </div>
      ) : (
        <NewTagInputContainer onClick={(e): void => e.stopPropagation()}>
          <NewTagInputRow>
            <Input
              size="small"
              placeholder="Tag name"
              value={newTagName}
              onChange={(e): void => setNewTagName(e.target.value)}
              onKeyDown={(e): void => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreateTag();
                }
              }}
              style={{ flex: 1 }}
              autoFocus
            />
            <SmallGreenButton
              onClick={handleCreateTag}
              disabled={!newTagName.trim()}
              style={{ opacity: !newTagName.trim() ? 0.5 : 1 }}
            >
              Add
            </SmallGreenButton>
          </NewTagInputRow>
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 13, color: greyText, marginBottom: 6 }}>
              Color
            </div>
            <ColorGrid>
              {PRESET_TAG_COLORS.map((color) => (
                <ColorSwatch
                  key={color}
                  $color={color}
                  $isSelected={selectedColor === color}
                  onClick={(e): void => {
                    e.stopPropagation();
                    setSelectedColor(color);
                  }}
                />
              ))}
            </ColorGrid>
          </div>
        </NewTagInputContainer>
      )}
    </div>
  );

  // Custom tag render for selected items
  const tagRender = ({
    value: tagValue,
    closable,
    onClose,
  }: {
    value: string;
    closable: boolean;
    onClose: () => void;
  }): JSX.Element => {
    // Guard against undefined value
    if (!tagValue) return <span />;

    // Find the tag info
    let tagInfo: { display_name: string; color: string } | null = null;

    if (tagValue.startsWith("__new_")) {
      const newTag = newTags.find(
        (t, i) => `__new_${i}_${t.display_name}` === tagValue,
      );
      if (newTag && newTag.display_name && newTag.color) {
        tagInfo = { display_name: newTag.display_name, color: newTag.color };
      }
    } else {
      const existingTag = tagMap.get(tagValue);
      if (existingTag) {
        tagInfo = {
          display_name: existingTag.display_name,
          color: existingTag.color,
        };
      }
    }

    if (!tagInfo) return <span />;

    return (
      <Tag
        color={tagInfo.color}
        closable={closable}
        onClose={onClose}
        style={{
          marginRight: 3,
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        {tagInfo.display_name}
      </Tag>
    );
  };

  // Get the values to pass to Select (combining existing IDs and temp IDs for new tags)
  const selectValue = useMemo(() => {
    const values: string[] = [...selectedTagIds];
    newTags.forEach((t, i) => {
      values.push(`__new_${i}_${t.display_name}`);
    });
    return values;
  }, [selectedTagIds, newTags]);

  return (
    <Select
      mode="multiple"
      placeholder={getFieldConfig(FormFieldName.TAGS).placeholder}
      options={tagOptions}
      value={selectValue}
      onChange={handleChange}
      loading={isLoading}
      allowClear
      showSearch
      optionLabelProp="label"
      tagRender={tagRender}
      dropdownRender={dropdownRender}
      dropdownStyle={{ width: 280 }}
      onDropdownVisibleChange={(open): void => {
        if (!open) {
          setShowCreateForm(false);
          setNewTagName("");
        }
      }}
      filterOption={(input, option): boolean => {
        const tag = availableTags.find((t) => t.id === option?.value);
        return tag
          ? tag.display_name.toLowerCase().includes(input.toLowerCase())
          : false;
      }}
    />
  );
};

const SDGOption = (id: number, name: string): JSX.Element => (
  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
    <Icon
      src={getSdgIcon(id)}
      alt={`SDG ${id}`}
      style={{ width: "24px", height: "24px" }}
    />
    <span>
      SDG {id}: {name}
    </span>
  </div>
);
